import { useLoadScript } from "@react-google-maps/api";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { assets } from "../../../Assets/assets";
import axios from "../../../Utils/axiosInstance";
import CommonButton from "../../Common/CommonButton";
import AddAddressModalAPI from "./Components/AddAddressModalAPI";
import AddressSelectModal from "./Components/AddressSelectModal";
import { useAuth } from "../../Context/AuthContext";

const LocationSkeleton = () => (
  <div className="mb-5 md:mb-6 flex items-center gap-3 mx-auto bg-white px-3 py-3 rounded-2xl max-w-[19rem] border border-gray-200 animate-pulse">
    {/* Icon Skeleton */}
    <div className="w-8 h-8 rounded-xl bg-gray-200" />
    {/* Text Skeleton */}
    <div className="flex-1 space-y-2">
      <div className="h-2 w-16 bg-gray-200 rounded" />
      <div className="h-3 w-32 bg-gray-300 rounded" />
    </div>
    {/* Chevron Skeleton */}
    <div className="w-4 h-4 bg-gray-200 rounded" />
  </div>
);

const AuthHero = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ["places"],
  });

  const [addresses, setAddresses] = useState([]);
  const [defaultAddress, setDefaultAddress] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addressType, setAddressType] = useState("Office");
  const [editAddress, setEditAddress] = useState(null);
  const [loadingLocations, setLoadingLocations] = useState(true);

  const fetchAddresses = async () => {
    try {
      setLoadingLocations(true);
      const res = await axios.get("/address");
      const data = res?.data?.data || [];
      setAddresses(data);
      const def = data.find((a) => a.isDefault);
      setDefaultAddress(def);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingLocations(false);
    }
  };

  useEffect(() => {
    fetchAddresses();
  }, []);

  return (
    <div
      className="relative bg-[#F5F5F5] overflow-hidden md:bg-right-top bg-center bg-no-repeat bg-cover"
      style={{ backgroundImage: `url(${assets.herobannernew})` }}
    >
      <div className="absolute inset-0 bg-gradient-to-r from-primaryColor/20 via-transparent to-primaryColor/20 z-0" />

      {/* CONTENT */}
      <div className="relative z-10 2xl:max-w-7xl mx-auto text-center px-4 py-24">
        {loadingLocations ? (
          <LocationSkeleton />
        ) : defaultAddress ? (
          <div
            onClick={() => setShowModal(true)}
            className="mb-5 md:mb-6 flex items-center gap-3 mx-auto bg-primaryColor px-2 py-2 rounded-2xl cursor-pointer hover:bg-primaryColor/90 active:scale-[0.98] transition-all duration-200 group max-w-[19rem]"
          >
            {/* Pin icon */}
            <div className="w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
              <svg
                className="w-4 h-4 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </div>

            {/* Text */}
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-white/60">
                Delivering to
              </p>
              <p className="text-sm font-bold text-white truncate leading-tight mt-0.5">
                {defaultAddress.addressLine1}
              </p>
            </div>

            {/* Chevron */}
            <svg
              className="w-4 h-4 text-white/60 group-hover:text-white flex-shrink-0 transition-colors duration-200"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        ) : (
          <div
            onClick={() => setShowModal(true)}
            className="mb-5 md:mb-6 flex mx-auto items-center gap-3 bg-primaryColor/10 border-2 border-primaryColor/30 px-2 py-2 rounded-2xl cursor-pointer hover:bg-primaryColor hover:border-primaryColor active:scale-[0.98] transition-all duration-200 group max-w-[19rem]"
          >
            {/* Plus icon */}
            <div className="w-9 h-9 rounded-xl bg-primaryColor/15 group-hover:bg-white/20 flex items-center justify-center flex-shrink-0 transition-colors duration-200">
              <svg
                className="w-4 h-4 text-primaryColor group-hover:text-white transition-colors duration-200"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 4v16m8-8H4"
                />
              </svg>
            </div>

            {/* Text */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-primaryColor group-hover:text-white transition-colors duration-200">
                Add Delivery Address
              </p>
              <p className="text-xs text-primaryColor/60 group-hover:text-white/70 mt-0.5 transition-colors duration-200">
                Faster checkout with saved address
              </p>
            </div>

            {/* Chevron */}
            <svg
              className="w-4 h-4 text-primaryColor/40 group-hover:text-white flex-shrink-0 transition-colors duration-200"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        )}

        {/* HEADING */}
        <h1 className="text-4xl md:text-5xl lg:text-6xl md:max-w-[70%] xl:max-w-[60%] mx-auto font-semibold leading-tight bg-clip-text text-transparent bg-gradient-to-r from-[#3D0F1B] to-subPrimaryColor mb-5 md:mb-6">
          Welcome to the Fast-Lane of Secured Trade
        </h1>

        {/* SUBTEXT */}
        <p className="text-secondaryTextColor md:w-[50%] mx-auto text-xs md:text-sm lg:text-base mb-8 leading-relaxed">
          Experience lightning-fast quotations, the lowest prices in India, and
          a platform built for reliability, transparency, and growth.
        </p>

        {/* BUTTON */}
        <CommonButton
          onClick={() =>
            user?.role === "supplier"
              ? navigate("/supplier-rqm-requests")
              : navigate("/post-requirement")
          }
          label={
            user?.role === "supplier"
              ? "Go for RQM section"
              : "Ask for a Quick Quotation"
          }
        />
      </div>

      {/* MODALS */}
      <AddressSelectModal
        showModal={showModal}
        closeModal={() => setShowModal(false)}
        addresses={addresses}
        setAddresses={setAddresses}
        setDefaultAddress={setDefaultAddress}
        refreshAddresses={fetchAddresses}
        openAddModal={(type, data) => {
          setAddressType(type);
          setEditAddress(data || null);
          setShowModal(false);
          setTimeout(() => setShowAddModal(true), 250);
        }}
      />

      {isLoaded && (
        <AddAddressModalAPI
          showModal={showAddModal}
          closeModal={() => setShowAddModal(false)}
          onBack={() => {
            setShowAddModal(false);
            setTimeout(() => setShowModal(true), 250);
          }}
          refreshAddresses={fetchAddresses}
          type={addressType}
          editData={editAddress}
          addresses={addresses}
        />
      )}
    </div>
  );
};

export default AuthHero;
